import { useState } from "react";
import { ClipboardList, ChevronDown, ChevronUp } from "lucide-react";

/**
 * IntakeAnswersBlock — expandable summary of a client's intake answers
 * on a booking card. Renders nothing if the booking has no answers.
 *
 * Providers see this on their /wallet booking list so they walk into
 * every session with the context the client already gave them.
 */
export default function IntakeAnswersBlock({ booking }) {
  const [open, setOpen] = useState(false);
  const answers = booking?.intakeAnswers;
  const questions = booking?.service?.intakeQuestions || [];
  if (!answers || Object.keys(answers).length === 0) return null;

  const qById = Object.fromEntries(questions.map((q) => [q.id, q]));
  const rows = Object.entries(answers)
    .map(([qid, val]) => {
      const q = qById[qid] || {};
      const label = q.label || qid;
      const display = Array.isArray(val) ? val.join(", ") : String(val ?? "");
      return { label, display };
    })
    .filter((r) => r.display);
  if (rows.length === 0) return null;

  return (
    <div className="border-t border-secondary/15 pt-3" data-testid={`intake-answers-${booking.id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-secondary hover:text-secondary/80"
        data-testid={`intake-toggle-${booking.id}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <ClipboardList className="w-3.5 h-3.5" />
          Intake answers · {rows.length}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <dl className="mt-3 space-y-2.5">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[minmax(120px,180px)_1fr] gap-3 items-start">
              <dt className="text-[10px] uppercase tracking-wider text-foreground/50 pt-0.5">
                {r.label}
              </dt>
              <dd className="text-sm text-foreground/85 whitespace-pre-wrap break-words">
                {r.display}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
